import { EsignatureType } from '~/enums/EsignatureType';

import {
  EsignatureIntegrationType,
  IntegrationModalKey,
  MODAL_KEYS,
} from '../../types';
import {
  ConfirmDeleteModal,
  ConfirmDeleteModalProps,
} from './ConfirmDeleteModal';
import { ConfirmRevokeModal } from './ConfirmRevokeModal';
import {
  ConfirmUpdateModal,
  ConfirmUpdateModalProps,
} from './ConfirmUpdateModal';
import {
  ManageUserSideDrawer,
  ManageUserSideDrawerProps,
} from './ManageUserSideDrawer';
import { useIntegrationsModalController } from './useIntegrationsModalController';

type ModalConfig<TProps> = {
  Component: React.ComponentType<any>;
  props: TProps;
};

function createModalConfig<TProps>(config: ModalConfig<TProps>) {
  return config;
}

export const IntegrationsModal = () => {
  const {
    activeModal,
    closeModal,
    closeAndClearSelectedUsers,
    setModalState,
    selectedUsers,
    selectedUserCount,
  } = useIntegrationsModalController();
  const modalConfigMap: Record<IntegrationModalKey, ModalConfig<any>> = {
    update: createModalConfig<Omit<ConfirmUpdateModalProps, 'onHide'>>({
      Component: ConfirmUpdateModal,
      props: {
        isVisible: true,
        integrationType:
          activeModal?.integrationType as EsignatureIntegrationType,
        providerName: (activeModal?.providerName ?? '') as EsignatureType,
        integrationId: activeModal?.integrationId ?? '',
      },
    }),
    delete: createModalConfig<Omit<ConfirmDeleteModalProps, 'onHide'>>({
      Component: ConfirmDeleteModal,
      props: {
        isVisible: true,
        providerName: (activeModal?.providerName ?? '') as EsignatureType,
        integrationId: activeModal?.integrationId ?? '',
      },
    }),
    manage: createModalConfig<ManageUserSideDrawerProps>({
      Component: ManageUserSideDrawer,
      props: {
        disablePrimary: selectedUserCount < 1,
        integrationType:
          activeModal?.integrationType as EsignatureIntegrationType,
        providerName: activeModal?.providerName ?? '',
        onHide: closeAndClearSelectedUsers,
        onRevokeClick: () =>
          setModalState({
            name: MODAL_KEYS.REVOKE,
            integrationId: activeModal?.integrationId ?? '',
          }),
      },
    }),
    revoke: createModalConfig({
      Component: ConfirmRevokeModal,
      props: {
        count: selectedUserCount,
        integrationId: activeModal?.integrationId,
        selectedUsers,
        onConfirmRevoke: closeAndClearSelectedUsers,
        onHide: () =>
          setModalState({
            name: MODAL_KEYS.MANAGE,
            integrationId: activeModal?.integrationId ?? '',
          }),
      },
    }),
  };

  if (!activeModal) return null;

  const { Component, props } = modalConfigMap[activeModal.name];

  return <Component {...props} onHide={props.onHide ?? closeModal} />;
};
